import React, { useState, useEffect, useCallback } from 'react';

import { QuantityCounter } from './QuantityCounter';

type Props = {
  id?: string,
  size?: 'large' | 'small',
  count: number,
  filled?: boolean,
  step: number,
  hasNextStep?: boolean,
  onIncrease: (amount: number) => void,
  onDecrease: (amount: number) => void,
  quantityLimit: number,
  isEditable?: boolean,
};

export const ManualQuantityCounter = ({
  id,
  count,
  size,
  filled,
  step,
  hasNextStep = true,
  onIncrease,
  onDecrease,
  quantityLimit,
  isEditable = false,
}: Props) => {
  const [manualCount, setManualCount] = useState(count);
  const [isEditing, setIsEditing] = useState(false);
  const [isEnteredAmountExceeded, setIsEnteredAmountExceeded] = useState(false);

  const handleInputBlur = () => {
    setIsEditing(false);
    if (isEditing && manualCount !== count) {
      changeAmount(manualCount);
    }
  };

  const handleChangeAmount = (value: number) => {
    if (!value) {
      setManualCount(count);
      setIsEnteredAmountExceeded(false);
      return;
    }

    const isValidAmount = /([1-9][0-9]*$)/.test(String(value));
    const updatedCount = isValidAmount ? Number(value) : count;

    const maxExceeded = updatedCount > quantityLimit;
    setIsEnteredAmountExceeded(maxExceeded);
    setManualCount(maxExceeded ? count : updatedCount);
  };

  const handleIncrease = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    changeAmount(count + 1);
  };

  const handleDecrease = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    changeAmount(Math.max(count - 1, 0));
  };

  const changeAmount = useCallback(
    (amount: number) => {
      const diff = amount - count;
      if (diff >= 1) {
        onIncrease(diff);
      } else {
        onDecrease(Math.abs(diff));
      }
    },
    [count, onDecrease, onIncrease]
  );

  useEffect(() => {
    setManualCount(count);
  }, [count]);

  return (
    <QuantityCounter
      id={id}
      filled={filled}
      size={size}
      step={step}
      counter={manualCount}
      hasNextStep={hasNextStep}
      hasCounter
      onIncrease={handleIncrease}
      onDecrease={handleDecrease}
      onChangeAmount={handleChangeAmount}
      reachedMax={quantityLimit ? count >= quantityLimit : false}
      enteredAmountExceeded={isEnteredAmountExceeded}
      isEditable={isEditable}
      isEditing={isEditing}
      onInputFocus={() => setIsEditing(true)}
      onInputBlur={handleInputBlur}
    />
  );
};
